/**
 * Auth session (token) management — the genuine *client* half of auth,
 * deliberately kept out of the `['auth','me']` server query.
 *
 * The token is the source of truth for "is there a session". The apiClient
 * reads it per-request; the auth/me query gates its `enabled` on it. It lives
 * in `localStorage` (legacy mode) or an HttpOnly cookie the server manages
 * (cookie-mode, gated on VITE_AUTH_COOKIE_MODE — backend not shipped yet).
 *
 * Reachable outside React (apiClient, cross-tab listener) — no imports of the
 * query cache or stores, so it can't create a cycle.
 */

import { paths } from '@/constants/paths';

const TOKEN_KEY = 'token';

function cookieMode(): boolean {
  return import.meta.env.VITE_AUTH_COOKIE_MODE === 'true';
}

/** The current session token, or undefined. In cookie-mode the token is in an
 *  HttpOnly cookie JS can't read, so this returns undefined by design. */
export function getToken(): string | undefined {
  if (cookieMode()) return undefined;
  try {
    return localStorage.getItem(TOKEN_KEY) ?? undefined;
  } catch {
    return undefined; // localStorage can throw in privacy modes
  }
}

/** Persist (or, with undefined, clear) the session token. No-op in cookie-mode
 *  (the server's Set-Cookie owns it). */
export function setToken(token: string | undefined): void {
  if (cookieMode()) return;
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // privacy mode — nothing we can safely do
  }
}

export function clearToken(): void {
  // Defensive: always remove the legacy localStorage token, even in cookie-mode
  // (where the server also expires the cookie), so no stale token can linger.
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // privacy mode — nothing we can safely do
  }
}

/**
 * Install a cross-tab de-auth listener (security-critical): when another tab
 * clears the session token (logout), `onDeauth` runs in this tab so it can wipe
 * the query cache, staff impersonation, and banners. De-auth ONLY — a populated
 * localStorage payload is never trusted (a forged blob could otherwise
 * escalate). Returns a disposer. Call once from main.tsx with `clearSession`.
 */
export function installCrossTabDeauth(onDeauth: () => void): () => void {
  const handler = (evt: StorageEvent) => {
    if (evt.key === TOKEN_KEY && evt.newValue === null) {
      onDeauth();
    }
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}

/**
 * Whether a session exists — the synchronous gate ProtectedRoute and the
 * auth/me query use so they don't flicker on reload. In cookie-mode we can't
 * see the HttpOnly cookie, so we optimistically report true and let the query
 * resolve (a 401 then clears it).
 */
export function hasSession(): boolean {
  return cookieMode() ? true : getToken() !== undefined;
}

/**
 * Is the browser sitting on a token-exchange ARRIVAL — `/sso?auth_token=…`?
 *
 * A magic-link / OAuth arrival carries a freshly minted token in the URL, and
 * that token supersedes whatever session this device still holds. Until `Sso`
 * has redeemed it, nothing else may act on the stored session: a background
 * `getMe()` fired with a STALE token 401s, the client's 401 branch hard-
 * navigates to `/login/signIn?reason=session_rejected`, and the page unloads
 * before the arrival token is ever exchanged — the person is dumped back on
 * the login screen and a bogus `session_rejected` report is filed (#268
 * follow-up, found in live browser verification).
 *
 * Two forms:
 *   - Legacy: `/sso?auth_token=…` (token already minted on the GET redirect).
 *   - Exchange (vingapi#372): `/sso?method_id=…&key=…` (the SPA stages and
 *     completes via POST; no token in the URL). Both are exchange arrivals
 *     because neither has a usable stored session — using the stale token
 *     before the exchange completes would cause the same bounce.
 *
 * The stored session itself is kept until the exchange result is known
 * (#719): `Sso` replaces it only on success and, on a failed arrival,
 * validates it (skipping the bounce) instead of signing the person out.
 *
 * The replay-recovery path (`/sso?error=…`, vingreact#102) has no token of its
 * own and *depends* on the stored session being usable via `getMe()`, so it
 * must stay enabled.
 *
 * Pure and synchronous (reads `window.location`), so query `enabled` gates can
 * call it during render.
 */
export function isTokenExchangeArrival(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const { pathname, search } = window.location;
    const normalized = pathname.replace(/\/+$/, '') || '/';
    if (normalized !== paths.sso) return false;
    const params = new URLSearchParams(search);
    if (params.get('auth_token') !== null) return true;
    // Exchange form: both method_id AND key must be present.
    if (params.get('method_id') !== null && params.get('key') !== null) return true;
    return false;
  } catch {
    return false;
  }
}

/** localStorage key for the cross-tab session signal (the token). */
export const TOKEN_STORAGE_KEY = TOKEN_KEY;
